/**
 * @file PatientLandingView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import TableContainer from "@mui/material/TableContainer";
import { Button, Dialog, DialogContent, Grid, Paper, Table, Typography } from "@mui/material";
import "./PatientHome.css"
import { PatientLandingViewModel } from "../viewmodels/PatientLandingViewModel";
import { PatientQRView } from "../../qrcode/views/PatientQRView";
import { NewPatientViewController } from "../controllers/NewPatientViewController";
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { ThemeProvider } from "@emotion/react";
import { noTextTransform, roboto700Text16Bold } from "../../../utils/theme/CommonTheme";
import CustomPatientRows from "./CustomPatientRows";
import { AppProgressBar } from "../../../utils/AppProgressBar";
import { Clinic } from "../../../../services/models/Clinic";
import { GetPatientResponse } from "../../../../services/models/GetPatientResponse";
import { ApiError } from "../../../../services/models/ApiError";
import Snackbar from "@mui/material/Snackbar";
import { CSSTransition } from "react-transition-group";
import { AppAlert } from "../../../utils/AppAlert";
import { TempPassword } from "../../../../services/network/ApiService";
import { BaseComponent } from "../../base/BaseComponent";
import { DeletePatientView } from "./DeletePatientView";
import { PatientRow } from "../../../../services/models/PatientRow";
import { NoDataFound } from "../../../common/customviews/NoDataFound";
import { NoPatientFound, PatientDeleteSuccsess } from "../../../utils/AppConstant";
import { PatientHeaderView } from "./PatientHeaderView";

/**
 * A type of Component state
 */
type CompState = {
    isNewPatientDialogOpen: boolean, isQrDialogOpen: boolean,
    qrloading: boolean, formattedContent: string, username: string,
    mainLoadding: boolean, isDeletePatientDialogOpen: boolean, isDataLoaded: boolean,
    showSnackBar: boolean, snackBarErrorMessage: string, clinic: Clinic,
    reloadPage: boolean, patientRow?: PatientRow, snackBarError: boolean
};

/**
 * An interface definition for Component Props
 */
interface Props {
    viewModel: PatientLandingViewModel
}


/**
 * A class definition for PatientListView
 */
export class PatientLandingView extends BaseComponent<Props, CompState> {


    readonly time = 2 * 24 * 60 * 60;
    viewModel: PatientLandingViewModel
    constructor(props: Props) {
        super(props)
        this.logger.verbose("PatientListView ()", PatientLandingView.name);
        this.viewModel = new PatientLandingViewModel();
        const selectedClinic = this.getSelectedClinic();
        let clinic: Clinic = new Clinic();

        if (!this.isEmpty(selectedClinic)) {
            clinic = JSON.parse(selectedClinic) as Clinic;
            this.logger.verbose(`PatientListView (${JSON.stringify(clinic)})`, PatientLandingView.name);
        }

        this.state = {
            isNewPatientDialogOpen: false, isQrDialogOpen: false,
            qrloading: false, formattedContent: "", username: "",
            mainLoadding: false, isDeletePatientDialogOpen: false,
            isDataLoaded: false, patientRow: undefined,
            showSnackBar: false, reloadPage: false,
            snackBarErrorMessage: "Unknown Error", clinic: clinic, snackBarError: false
        };

        this.getPatientResponse = new GetPatientResponse();
        this.getPatientResponse.rows = [];
    }

    getPatientResponse: GetPatientResponse;

    /**
     * LifeCycle method
     */
    async componentDidMount(): Promise<void> {
        this.logger.verbose(`PatientListView (mounted)`, PatientLandingView.name);
        this.subscribeApiEvenets();
        if (!this.isLoggedIn()) {
            return;
        }
        this.loadPatients(this.state.clinic?.id!!); // eslint-disable-line
    }

    async componentWillUnmount(): Promise<void> {
        this.unSubscribeApiEvenets();
    }

    /**
     * Loads patients
     * @param clinicId string
     * @returns 
     */
    private loadPatients = (clinicId: string) => {
        this.dataReLoad();
        const accessToken = this.validateAndGetSession(PatientLandingView.name);
        this.viewModel.getPatientList(clinicId, accessToken, 0);
    }

    /**
     * Subscribes events
     */
    private subscribeApiEvenets = () => {
        this.viewModel.onLoadedPatientList.subscribe(data => {
            this.getPatientResponse = data;
            this.logger.verbose(`loadPatients (${JSON.stringify(data)})`, PatientLandingView.name);
            this.dataLoaded();
        });
        this.viewModel.onError.subscribe((error: ApiError) => {
            this.dataLoaded();
            this.showSnackBarError(error?.message!!, true); // eslint-disable-line
        });

        this.viewModel.onLoadQrCode.subscribe(response => {
            this.hideQrLoading();
            this.hideMainLoading();
            this.createQRContent(response.data!!, this.state.username); // eslint-disable-line
        });

        this.viewModel.onLoadQrCodeError.subscribe(error => {
            this.hideQrLoading()
            this.hideMainLoading();
            this.logger.error(`viewQrCode(${error.message})`, PatientLandingView.name);
            this.showSnackBarError(error?.message!!, true); // eslint-disable-line
        });

        this.viewModel.onPatientDeleted.subscribe(response => {
            this.logger.verbose(`performPatientDelete(${JSON.stringify(response)})`, PatientLandingView.name);
            this.hideMainLoading();

            this.showSnackBarError(PatientDeleteSuccsess, false); // eslint-disable-line
            this.loadPatients(this.state.clinic?.id!!); // eslint-disable-line
        });

        this.viewModel.onPatientDeletedError.subscribe(error => {
            this.hideMainLoading();
            this.logger.error(`performPatientDelete(${JSON.stringify(error)})`, PatientLandingView.name);
            this.showSnackBarError(error?.message!!, true); // eslint-disable-line
        });
    }

    /**
     * Unsubscribes events
     */
    private unSubscribeApiEvenets = () => {
        this.viewModel.onLoadedPatientList.unsubscribe();
        this.viewModel.onError.unsubscribe();
    }

    /**
     * Dismisses new patient dialog
     */
    public dismissNewPatientDialog = () => {
        this.setState({ isNewPatientDialogOpen: false });
    }

    /**
     * Shows new patient dialog
     */
    private showNewPatientDialog = () => {
        this.setState({ isNewPatientDialogOpen: true });
    }

    /**
     * Sets state to data reload
     */
    private dataReLoad = () => {
        this.setState({ isDataLoaded: false });
    }

    /**
     * Sets state to data load
     */
    private dataLoaded = () => {
        this.setState({ isDataLoaded: true });
    }

    /**
     * Handles QR close
     * @returns 
     */
    private handleQRClose = () => {
        if (this.state.reloadPage) {
            this.setState({
                isQrDialogOpen: !this.state.isQrDialogOpen,
                reloadPage: false
            });
            this.loadPatients(this.state.clinic?.id!!); // eslint-disable-line
            return;
        }
        this.setState({
            isQrDialogOpen: !this.state.isQrDialogOpen,
        });
    };

    /**
     * Opens delete patient dialog
     * @param row PatientRow
     */
    private openDeletePatientDialog = (row: PatientRow) => {
        this.setState({ isDeletePatientDialogOpen: true, patientRow: row });
    };

    /**
     * Dismisses delete patient dialog
     */
    private dismissDeletePatientDialog = () => {
        this.setState({ isDeletePatientDialogOpen: false });
    }

    /**
     * Toggles QR loading flag
     */
    private showQrLoading = () => this.setState({ qrloading: true });

    /**
     * Toggles QR loading flag
     */
    private hideQrLoading = () => this.setState({ qrloading: false });

    /**
     * Toggle main loading flag
     */
    private showMainLoading = () => this.setState({ mainLoadding: true });

    /**
     * Toggle main loading flag
     */
    private hideMainLoading = () => this.setState({ mainLoadding: false });

    /**
     * Sets username
     * @param username string
     */
    private setUsername = (username: string) => this.setState({ username: username });

    /**
     * Hides the snack bar
     */
    private hideSnackBarError = () => this.setState({ showSnackBar: false, snackBarError: false })

    /**
     * Shows the snack bar
     * @param message string
     */
    private showSnackBarError = (message: string, snackBarError: boolean) => {
        this.setState({ showSnackBar: true, snackBarErrorMessage: message, snackBarError: snackBarError })
    }

    /**
     * Triggers on selection of the clinic
     * @param clinic Clinic
     */
    public onClinicSelected = (clinic: Clinic) => {
        this.logger.warning(`onClinicSelected(${JSON.stringify(clinic)})`, PatientLandingView.name);
        this.setState({ clinic: clinic });
        this.loadPatients(clinic.id!!); // eslint-disable-line
    }

    /**
     * Views QR code
     * @param username string
     */
    private viewQrCode = async (username: string) => {
        this.showQrLoading();
        this.showMainLoading()
        this.setUsername(username);
        const accessToken = this.validateAndGetSession(PatientLandingView.name);
        this.viewModel.refreshQr(username, accessToken);
    }

    /**
     * Performs patient delete
     * @param row PatientRow
     * @returns 
     */
    private performPatientDelete = async (row: PatientRow) => {
        this.showMainLoading()
        const accessToken = this.validateAndGetSession(PatientLandingView.name);
        this.viewModel.deletePatient(row.id!!, accessToken); // eslint-disable-line
    }

    /**
     * Tigger on creation of new patient
     * @param id string
     */
    private onNewPatientCreated = (id: string) => {
        this.logger.verbose(`onNewPatientCreated(${id})`, PatientLandingView.name);
        this.setState({ reloadPage: true });
        this.viewQrCode(id);
    }

    /**
     * Tigger on deletion of the patient
     */
    private onPatientDeleted = () => {
        this.logger.warning(`onPatientDeleted()`, PatientLandingView.name);
        this.setState({ isDeletePatientDialogOpen: false });
        this.performPatientDelete(this.state.patientRow!!); // eslint-disable-line
    }

    /**
     * Creates QR content
     * @param tempPassword TempPassword
     * @param username string
     */
    private createQRContent = (tempPassword: TempPassword, username: string) => {
        const time = new Date(Date.now() + this.time * 1000).toISOString()
        const qrContent = {
            expiredTime: time, id: username,
            password: tempPassword.rawPassword, role: "Patient",
            username: `Anonymous:${username}`
        }

        this.setState({ username: username, formattedContent: JSON.stringify(qrContent), isQrDialogOpen: true });

        this.hideMainLoading();
        this.hideQrLoading();
    }

    /**
     * Gets current patient
     * @returns 
     */
    private getCurrentPatient = () => this.state.patientRow!!  // eslint-disable-line

    render() {

        if (this.state.isDataLoaded) {
            this.logger.verbose("Patint present in the list", PatientLandingView.name);
            return <React.Fragment>
                <Grid className="PatientTableBox">
                    <Grid container spacing={0} >
                        <Grid item xs={5} container>
                            <ThemeProvider theme={roboto700Text16Bold}>
                                <Typography variant="h5" className="PatientTableContainerHeader"> Patient records ({this.getPatientResponse?.count})</Typography>
                            </ThemeProvider>
                        </Grid>
                        <Grid item xs={7} container justifyContent="flex-end">
                            <ThemeProvider theme={noTextTransform}>
                                <Button onClick={this.showNewPatientDialog} size="small" variant="contained"
                                    disableElevation disabled={false} className="AddNewRecordButton">
                                    + &nbsp;&nbsp;  Add a Patient Record
                                </Button>
                            </ThemeProvider>
                        </Grid>
                    </Grid>

                    {
                        // eslint-disable-next-line
                        this.getPatientResponse?.rows?.length == 0 && <NoDataFound message={NoPatientFound} />
                    }
                    {
                        // eslint-disable-next-line
                        (this.getPatientResponse?.rows!!.length > 0) && <TableContainer className="TableContainer" component={Paper} sx={{ width: '100%' }}>
                            <Table sx={{ minWidth: 500, display: "block" }} aria-label="custom pagination table" className="PatientTable">
                                <PatientHeaderView />
                                <CustomPatientRows viewQrCode={this.viewQrCode} deletePatient={this.openDeletePatientDialog} getPatientResponse={this.getPatientResponse!}
                                    clinicId={this.state.clinic.id}
                                />
                            </Table>
                        </TableContainer>}
                    <Dialog  className="AddPatientBoxContainer" onClose={this.dismissNewPatientDialog} aria-labelledby="customized-dialog-title" open={this.state.isNewPatientDialogOpen} fullWidth={true}
                        maxWidth="sm" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                        <DialogContent style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <NewPatientViewController onDialogDismissed={this.dismissNewPatientDialog} onNewPatientCreated={this.onNewPatientCreated} />
                        </DialogContent>
                    </Dialog>


                    <Dialog onClose={this.dismissDeletePatientDialog} aria-labelledby="customized-dialog-title" open={this.state.isDeletePatientDialogOpen} fullWidth={false}
                        maxWidth="sm" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                        <DialogContent dividers style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <DeletePatientView onDialogDismissed={this.dismissDeletePatientDialog} onUserRemoved={this.onPatientDeleted} row={this.getCurrentPatient()} />
                        </DialogContent>
                    </Dialog>


                    <Dialog onClose={this.handleQRClose} aria-labelledby="customized-dialog-title" open={this.state.isQrDialogOpen}
                        maxWidth="sm" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                        <DialogContent dividers style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <PatientQRView qrContent={this.state.formattedContent} qrUserName={this.state.username} qrloading={this.state.qrloading} onDialogDismissed={this.handleQRClose} />
                        </DialogContent>
                    </Dialog>
                </Grid >
                <CSSTransition
                    in={this.state.showSnackBar}
                    timeout={300}
                    classNames="alert"
                    unmountOnExit>
                    <Snackbar open={this.state.showSnackBar} autoHideDuration={6000} onClose={this.hideSnackBarError}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                        <AppAlert onClose={this.hideSnackBarError} severity={!this.state.snackBarError ? "success" : "error"} sx={{ width: "150%" }}>
                            {`${this.state.snackBarErrorMessage}`}
                        </AppAlert>
                    </Snackbar>
                </CSSTransition>
                <BackDropLoadingView handleBackDropClose={this.hideMainLoading} isLoading={this.state.mainLoadding} />
            </React.Fragment>
        } else {
            return <React.Fragment>
                <AppProgressBar color="primary" />
            </React.Fragment>
        }
    }
}

export default PatientLandingView;
